import React from 'react';
import { FiHeart, FiStar, FiUsers } from 'react-icons/fi';
import FadeInView from './FadeInView';

const ease = [0.22, 1, 0.36, 1];

const stats = [
  { value: '500', suffix: '+', label: 'Happy Customers' },
  { value: '5', suffix: '★', label: 'Average Rating' },
  { value: '99', suffix: '%', label: 'Time Accuracy' },
  { value: '24', suffix: '/7', label: 'Service Available' },
];

const whyUs = [
  { icon: FiStar, title: 'Premium Fleet', desc: 'Luxury Mercedes-Benz vehicles equipped with modern amenities for maximum comfort' },
  { icon: FiUsers, title: 'Local Expertise', desc: "Native drivers with intimate knowledge of Corfu's hidden gems and best routes" },
  { icon: FiHeart, title: 'Personal Service', desc: 'Dedicated to creating memorable experiences tailored to your unique needs' },
];

const AboutUs = () => {
  return (
    <section id="about" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16 lg:mb-20"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Who We Are</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            About <span className="italic text-[#ba1609]">Lasmari Chauffeurs</span>
          </h2>
        </FadeInView>

        {/* Editorial split: story text + layered photo-in-photo */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-24 lg:mb-28">
          {/* Story */}
          <FadeInView
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Our Story</p>
            <h3 className="text-2xl lg:text-4xl font-light text-[#1a1012] mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              From Restaurant <span className="italic text-[#ba1609]">to Road</span>
            </h3>
            <div className="space-y-4 text-[#5b4d4f] text-sm lg:text-base leading-relaxed font-light">
              <p>
                Founded in 2023, <span className="text-[#1a1012] font-medium">Lasmari Chauffeurs</span> began as a simple solution to help transport guests to and from our restaurant. What started as a small convenience has since grown into the leading transfer company in the northeastern region of Corfu.
              </p>
              <p>
                The name "Lasmari" carries the legacy of our restaurant, where hospitality, care, and a passion for exceptional service were born. That same spirit now drives our transportation services, rooted in local pride and a deep understanding of what makes a guest feel truly welcome.
              </p>
              <p>
                Today, we're proud to serve discerning visitors who appreciate comfort, reliability, and the personal touch that makes all the difference in creating unforgettable experiences across Corfu.
              </p>
            </div>
          </FadeInView>

          {/* Layered photo composition */}
          <FadeInView
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="relative"
          >
            {/* Primary framed photo */}
            <div className="relative overflow-hidden rounded-[20px] shadow-2xl shadow-black/25 ring-1 ring-black/5">
              <img
                src="/images/backgrounds/cfu_corfu_old_town.webp"
                alt="Corfu Old Town rooftops and the Ionian sea"
                className="w-full h-[380px] lg:h-[460px] object-cover"
                loading="lazy"
              />
            </div>

            {/* Smaller overlapping photo (photo-in-photo) */}
            <div className="absolute -bottom-10 -left-5 lg:-left-12 w-40 lg:w-56 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 border-[6px] border-white hidden sm:block">
              <img
                src="/images/backgrounds/coastal-drive-vclass.webp"
                alt="Mercedes-Benz V-Class on a coastal Corfu road"
                className="w-full h-44 lg:h-56 object-cover"
                loading="lazy"
              />
            </div>

            {/* Red 'Est.' badge */}
            <div className="absolute -top-6 -right-3 lg:-right-6 w-24 h-24 rounded-full bg-[#ba1609] text-white flex flex-col items-center justify-center shadow-xl shadow-[#ba1609]/30">
              <span className="text-2xl font-light leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>2023</span>
              <span className="text-[10px] uppercase tracking-[0.2em] mt-1 text-white/85">Established</span>
            </div>
          </FadeInView>
        </div>

        {/* Stats, editorial band */}
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="border-y border-black/10 py-10 lg:py-12 mb-24 lg:mb-28"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <FadeInView
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05, ease }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl text-[#1a1012] mb-1" style={{ fontFamily: "'Prata', serif" }}>
                  {stat.value}<span className="text-[#ba1609] text-2xl">{stat.suffix}</span>
                </div>
                <p className="text-[#5b4d4f] text-[11px] uppercase tracking-[0.25em]">{stat.label}</p>
              </FadeInView>
            ))}
          </div>
        </FadeInView>

        {/* Why Choose Us */}
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-12"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">The Difference</p>
          <h3 className="text-2xl lg:text-4xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Choose <span className="italic text-[#ba1609]">Us</span>
          </h3>
        </FadeInView>

        <div className="grid md:grid-cols-3 gap-6">
          {whyUs.map((item, i) => (
            <FadeInView
              key={item.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05, ease }}
              className="group bg-white border border-black/10 rounded-2xl p-8 hover:border-[#ba1609]/40 hover:shadow-xl hover:shadow-black/5 transition-[color,background-color,border-color,box-shadow] duration-500 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#ba1609]/10 border border-[#ba1609]/15 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#ba1609] transition-colors duration-500">
                <item.icon className="w-6 h-6 text-[#ba1609] group-hover:text-white transition-colors duration-500" />
              </div>
              <h4 className="text-[#1a1012] text-lg font-medium mb-3">{item.title}</h4>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">{item.desc}</p>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
